/**
 * Builds a `turbo:before-stream-render` handler that runs callbacks around Turbo's own render of
 * the stream, which waits for them. To only react once a stream has rendered, listen for
 * `turbo:custom:stream-render` instead (see `@/js/turbo/extensions`).
 * Works whether the handler is bound with `addEventListener` or with jQuery's `.on()`.
 * @example Usage
 *   $(document).on('turbo:before-stream-render', aroundStreamRender(saveScroll, restoreScroll));
 *
 * @param {Function|null} beforeFn called with the `<turbo-stream>` element right before it
 *   renders; the render waits for the promise it returns
 * @param {Function|null} afterFn called with the `<turbo-stream>` element once it has rendered;
 *   the render waits for the promise it returns
 * @return {Function} the event handler
 */
const aroundStreamRender = (beforeFn, afterFn) => (e) => {
  const { detail } = e.originalEvent || e; // make it work with both jQuery and plain Event
  const { render } = detail;
  detail.render = async function (stream) {
    await beforeFn?.(stream);
    const result = await render.call(this, stream);
    await afterFn?.(stream);
    return result;
  };
};

/**
 * Builds a `turbo:before-stream-render` handler that calls `func` right before the stream renders.
 * @example Usage
 *   $(document).on('turbo:before-stream-render', beforeStreamRender(() => console.log('before')));
 *
 * @param {Function} func called with the `<turbo-stream>` element; the render waits for the
 *   promise it returns
 * @return {Function} the event handler
 */
const beforeStreamRender = (func) => aroundStreamRender(func, null);

/**
 * Builds a `turbo:before-stream-render` handler that calls `func` once the stream has rendered.
 * @example Usage
 *   $(document).on('turbo:before-stream-render', afterStreamRender(() => console.log('after')));
 *
 * @param {Function} func called with the `<turbo-stream>` element; the render waits for the
 *   promise it returns
 * @return {Function} the event handler
 */
const afterStreamRender = (func) => aroundStreamRender(null, func);

export { aroundStreamRender, beforeStreamRender, afterStreamRender };
